import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { adminArticles, adminCreateArticle, adminUpdateArticle, adminDeleteArticle } from "../lib/api";
import { Toolbar, Modal, Field, Check, Card, inputCls } from "./AdminLayout";
import { Pencil, Trash2, Star, Zap, PenLine, ChevronRight, AudioLines } from "lucide-react";
import InterviewRecorder from "../components/InterviewRecorder";
import ImageUpload from "../components/ImageUpload";
import { useAuth } from "../context/AuthContext";
import MicButton from "../components/MicButton";
import { aiDraft } from "../lib/api";

const CATS = [
  { v: "news", l: "Local News" },
  { v: "sports", l: "High School Sports" },
  { v: "entertainment", l: "Entertainment" },
  { v: "columnists", l: "Columnists" },
];

const empty = {
  title: "", category: "news", excerpt: "", bodyText: "", image: "",
  author_name: "News Staff", author_role: "Newsroom", author_avatar: "",
  read_time: 3, premium: false, breaking: false, featured: false,
};

export default function ArticlesAdmin() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [aiNotes, setAiNotes] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [recOpen, setRecOpen] = useState(false);

  const load = () => adminArticles().then(setItems).catch(() => {});
  const [params, setParams] = useSearchParams();
  const handledNew = useRef(false);
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!params.get("new")) handledNew.current = false;
    if (params.get("new") && !handledNew.current) {
      handledNew.current = true;
      openNew();
      setParams({}, { replace: true });
    }
    const editId = params.get("edit");
    const target = editId && items.find((a) => a.id === editId);
    if (target) { openEdit(target); setParams({}, { replace: true }); }
  }, [params, items]); // eslint-disable-line react-hooks/exhaustive-deps

  const generateDraft = async () => {
    if (!aiNotes.trim()) return toast.error("Dictate or type some notes first.");
    setAiBusy(true);
    try {
      const d = await aiDraft({ notes: aiNotes, author_name: form.author_name, category: form.category });
      setForm((f) => ({
        ...f,
        title: d.title || f.title,
        excerpt: d.excerpt || f.excerpt,
        bodyText: (d.body || []).join("\n\n") || f.bodyText,
      }));
      toast.success("Draft ready — review and edit every line before publishing.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not build the draft. Please try again.");
    } finally {
      setAiBusy(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggle = (k) => () => setForm({ ...form, [k]: !form[k] });

  const openNew = () => { setEditing(null); setForm(isAdmin ? empty : { ...empty, author_name: user?.name || empty.author_name, author_role: "Staff Writer" }); setAiNotes(sessionStorage.getItem("nl_prefill_notes") || ""); setOpen(true); };
  const openEdit = (a) => {
    setEditing(a);
    setForm({ ...empty, ...a, bodyText: (a.body || []).join("\n\n") });
    setAiNotes("");
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error("Title is required.");
    const body = form.bodyText.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);
    const payload = {
      title: form.title, category: form.category, excerpt: form.excerpt,
      body: body.length ? body : [form.excerpt].filter(Boolean),
      image: form.image, author_name: form.author_name, author_role: form.author_role,
      author_avatar: form.author_avatar, read_time: Number(form.read_time) || 3,
      premium: false, breaking: form.breaking, featured: form.featured,
    };
    setBusy(true);
    try {
      if (editing) { await adminUpdateArticle(editing.id, payload); toast.success("Article updated."); }
      else { await adminCreateArticle(payload); toast.success("Article published."); }
      sessionStorage.removeItem("nl_prefill_notes");
      setOpen(false); load();
    } catch { toast.error("Could not save article."); }
    finally { setBusy(false); }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.title}"?`)) return;
    await adminDeleteArticle(a.id); toast.success("Article deleted."); load();
  };

  return (
    <div>
      <Toolbar title={isAdmin ? "Articles" : "My Stories"} subtitle={isAdmin ? "Write, edit and publish stories across every section" : "Only the stories you've written appear here"} onAdd={openNew} addLabel="New Article" />
      <div className="space-y-2 lg:hidden" data-testid="articles-mobile-list">
        {items.map((a) => (
          <button key={a.id} onClick={() => openEdit(a)} data-testid={`m-article-${a.id}`} className="flex w-full items-center gap-3 rounded-xl border border-slate-200 bg-white p-3 text-left active:bg-slate-50">
            {a.image ? <img src={a.image} alt="" onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} className="h-14 w-14 shrink-0 rounded-lg bg-slate-100 object-cover" /> : <span className="h-14 w-14 shrink-0 rounded-lg bg-slate-100" />}
            <div className="min-w-0 flex-1">
              <p className="line-clamp-2 text-sm font-semibold leading-snug text-[var(--navy)]">{a.title}</p>
              <p className="mt-0.5 flex items-center gap-1.5 text-[11px] capitalize text-slate-400">{a.category}{a.breaking && <span className="rounded bg-red-100 px-1 font-bold text-red-700">Breaking</span>}{a.featured && <span className="rounded bg-amber-100 px-1 font-bold text-amber-700">Featured</span>}</p>
            </div>
            <ChevronRight size={18} className="shrink-0 text-slate-300" />
          </button>
        ))}
      </div>
      <Card className="hidden overflow-hidden lg:block">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">Title</th>
                <th className="px-4 py-3">Section</th>
                <th className="px-4 py-3">Flags</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((a) => (
                <tr key={a.id} data-testid={`admin-article-${a.id}`} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {a.image && <img src={a.image} alt="" onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} className="h-10 w-14 rounded object-cover" />}
                      <span className="font-semibold text-[var(--navy)] line-clamp-1">{a.title}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 capitalize text-slate-600">{a.category}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      {a.featured && <Badge icon={Star} cls="bg-amber-100 text-amber-700">Featured</Badge>}
                      {a.breaking && <Badge icon={Zap} cls="bg-red-100 text-red-700">Breaking</Badge>}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      <IconBtn onClick={() => openEdit(a)} testid={`edit-${a.id}`}><Pencil size={15} /></IconBtn>
                      <IconBtn onClick={() => remove(a)} danger testid={`delete-${a.id}`}><Trash2 size={15} /></IconBtn>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {open && (
        <Modal title={editing ? "Edit Article" : "New Article"} onClose={() => { sessionStorage.removeItem("nl_prefill_notes"); setOpen(false); }} onSubmit={submit} busy={busy} submitLabel={editing ? "Save Changes" : "Publish"}>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="rounded-xl border border-[var(--gold)]/50 bg-amber-50/60 p-4 sm:col-span-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="flex items-center gap-2 font-semibold text-[var(--navy)]"><PenLine size={16} className="text-[var(--gold)]" /> Story Assist — talk through your story</p>
                <div className="flex flex-wrap gap-2">
                  <MicButton onText={(t) => setAiNotes((n) => (n ? n + " " : "") + t)} testid="ai-mic" />
                  <button type="button" onClick={() => setRecOpen(true)} data-testid="editor-record-interview" className="flex items-center gap-1.5 rounded-full border border-red-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50"><AudioLines size={14} /> Record interview</button>
                </div>
              </div>
              <textarea
                data-testid="ai-notes"
                value={aiNotes}
                onChange={(e) => setAiNotes(e.target.value)}
                rows={3}
                placeholder="Tap Dictate and talk through what happened, or type rough notes. You'll get a first draft in your own style — you review and edit every line before it publishes."
                className={`${inputCls} mt-3`}
              />
              <button type="button" onClick={generateDraft} disabled={aiBusy} data-testid="ai-generate" className="mt-2 flex items-center gap-2 rounded-lg bg-[var(--navy)] px-4 py-2 text-xs font-semibold uppercase tracking-wider text-white hover:bg-[var(--header-navy)] disabled:opacity-60">
                <PenLine size={14} /> {aiBusy ? "Building your draft…" : "Build a first draft"}
              </button>
            </div>
            <Field label="Headline" full><input data-testid="art-title" value={form.title} onChange={set("title")} className={inputCls} placeholder="Pirates win district opener…" /></Field>
            <Field label="Section">
              <select data-testid="art-category" value={form.category} onChange={set("category")} className={inputCls}>
                {CATS.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
              </select>
            </Field>
            <Field label="Read time (min)"><input type="number" min="1" data-testid="art-readtime" value={form.read_time} onChange={set("read_time")} className={inputCls} /></Field>
            <Field label="Excerpt / standfirst" full><textarea data-testid="art-excerpt" value={form.excerpt} onChange={set("excerpt")} rows={2} className={inputCls} /></Field>
            <Field label="Body (separate paragraphs with a blank line)" full>
              <div className="mb-1 flex justify-end"><MicButton onText={(t) => setForm((f) => ({ ...f, bodyText: (f.bodyText ? f.bodyText + " " : "") + t }))} testid="body-mic" label="Dictate body" /></div>
              <textarea data-testid="art-body" value={form.bodyText} onChange={set("bodyText")} rows={6} className={inputCls} />
            </Field>
            <Field label="Lead image (upload from phone or paste URL)" full>
              <ImageUpload value={form.image} onChange={(url) => setForm((f) => ({ ...f, image: url }))} testid="art-image" />
              <input data-testid="art-image-url" value={form.image} onChange={set("image")} className={`${inputCls} mt-2`} placeholder="…or paste an image URL" />
            </Field>
            <Field label="Author name"><input data-testid="art-author" value={form.author_name} onChange={set("author_name")} className={inputCls} /></Field>
            <Field label="Author role"><input value={form.author_role} onChange={set("author_role")} className={inputCls} /></Field>
            <div className="grid grid-cols-2 gap-2 sm:col-span-2">
              <Check label="Featured" checked={form.featured} onChange={toggle("featured")} testid="art-featured" />
              <Check label="Breaking" checked={form.breaking} onChange={toggle("breaking")} testid="art-breaking" />
            </div>
          </div>
        </Modal>
      )}
      {recOpen && <InterviewRecorder onClose={() => setRecOpen(false)} onUseTranscript={(t) => setAiNotes((n) => (n ? n + " " : "") + t)} />}
    </div>
  );
}

const Badge = ({ icon: Icon, cls, children }) => (
  <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-semibold ${cls}`}><Icon size={10} /> {children}</span>
);
const IconBtn = ({ children, onClick, danger, testid }) => (
  <button onClick={onClick} data-testid={testid} className={`rounded-lg border p-2 ${danger ? "border-red-200 text-red-600 hover:bg-red-50" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{children}</button>
);
export { Badge, IconBtn };
